<template>
  <div class="mfa-card">
    <div class="mfa-title">{{ t('components.security.mfa.passkey.title') }}</div>
    <div class="mfa-desc">
      <span v-if="status === 'idle'">{{ t('components.security.mfa.passkey.descIdle') }}</span>
      <span v-else-if="status === 'requesting'">{{
        t('components.security.mfa.passkey.descRequesting')
      }}</span>
      <span v-else-if="status === 'prompting'">{{
        t('components.security.mfa.passkey.descPrompting')
      }}</span>
      <span v-else-if="status === 'verifying'">{{
        t('components.security.mfa.passkey.descVerifying')
      }}</span>
      <span v-else-if="status === 'error'" class="text-danger-500">{{ errorMsg }}</span>
    </div>

    <div class="flex items-center gap-2 mt-2">
      <a-button
        type="primary"
        size="small"
        :disabled="status === 'requesting' || status === 'prompting' || status === 'verifying'"
        @click="startPasskeyVerify"
      >
        {{ t('components.security.mfa.passkey.ctaStart') }}
      </a-button>

      <a-button
        size="small"
        :disabled="status === 'requesting' || status === 'prompting'"
        @click="startPasskeyVerify"
      >
        {{ t('components.security.mfa.passkey.ctaRetry') }}
      </a-button>

      <slot name="fallback"></slot>
    </div>

    <div :class="verifyResult === true ? 'ink-12 text-success mt-2' : 'ink-12 text-ink-600 mt-2'">
      {{
        verifyResult === true
          ? t('components.security.mfa.passkey.hint.pass')
          : t('components.security.mfa.passkey.hint.tip')
      }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import i18n from '@/locales'
import type { Condition } from '../types'
import { useVerifierRunner } from '../useVerifierRunner'
import { toPublicKeyOptions, arrayBufferToB64url } from '@/utils'
const { t } = i18n.global as any

const props = defineProps<{
  condition: Condition
  passageKey: string
  securityCode: string
  resolvedInstanceId: string
  make: (c: Condition) => Promise<any>
  verifyData: { email: string; phone: string; loginType: number; unauthorized: boolean }
}>()

const emit = defineEmits<{ (e: 'verified', ok: boolean): void }>()

const verifyResult = ref<boolean | undefined>(undefined)
const status = ref<'idle' | 'requesting' | 'prompting' | 'verifying' | 'error'>('idle')
const errorMsg = ref('')

const { send, verify } = useVerifierRunner(
  props.condition,
  props.make,
  props.verifyData.unauthorized,
)

function mapWebAuthnError(err: any): string {
  const name = err?.name || ''
  const t = (i18n.global as any).t
  if (name === 'NotAllowedError') return t('components.security.mfa.passkey.errors.notAllowed')
  if (name === 'AbortError') return t('components.security.mfa.passkey.errors.abort')
  if (name === 'SecurityError') return t('components.security.mfa.passkey.errors.security')
  if (name === 'InvalidStateError') return t('components.security.mfa.passkey.errors.invalidState')
  return t('components.security.mfa.passkey.errors.generic')
}

async function startPasskeyVerify() {
  errorMsg.value = ''
  status.value = 'requesting'
  try {
    const reqJSON = await send()

    status.value = 'prompting'
    const publicKey = toPublicKeyOptions(reqJSON.options)
    const cred = (await navigator.credentials.get({ publicKey })) as PublicKeyCredential
    status.value = 'verifying'
    const assertion = cred.response as AuthenticatorAssertionResponse
    const payload = {
      id: cred.id,
      type: cred.type,
      rawId: arrayBufferToB64url(cred.rawId),
      response: {
        authenticatorData: arrayBufferToB64url(assertion.authenticatorData),
        clientDataJSON: arrayBufferToB64url(assertion.clientDataJSON),
        signature: arrayBufferToB64url(assertion.signature),
        userHandle: assertion.userHandle ? arrayBufferToB64url(assertion.userHandle) : null,
      },
      clientExtensionResults: cred.getClientExtensionResults?.(),
    }
    const ok = await verify({ code: JSON.stringify(payload) })
    verifyResult.value = ok
    emit('verified', !!ok)
    status.value = 'idle'
  } catch (err: any) {
    status.value = 'error'
    errorMsg.value = mapWebAuthnError(err)
  }
}
</script>
